<script setup>
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { DxList } from 'devextreme-vue/list'

import { useODataSource } from '@/composables/data-grid'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'

import { useWebSiteStore } from '@/stores/web-site.store'

import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import BudgetDocumentEdit from '@/components/accounting/BudgetDocumentEdit.vue'
import BudgetDocumentDetail from '@/components/accounting/BudgetDocumentDetail.vue'
import BudgetDocumentListItem from '@/components/accounting/BudgetDocumentListItem.vue'
import SignalBar from '@/components/shared/signal-bar/SignalBar.vue'

import { accountingApi } from '@/apis/accounting.api'

const webSiteStore = useWebSiteStore()

const isEmptyDataSource = ref(false)
const itemDetailId = ref(null)
const editItem = ref(null)

const quickCreate = ref(false)
const onAddClick = (quick = false) => { quickCreate.value = quick === true; editItem.value = {} }

webSiteStore.fabActions = [
  {
    id: 'add',
    icon: 'mdi-file-plus-outline',
    onClick: () => onAddClick(false),
  },
  {
    id: 'quick-create',
    icon: 'mdi-flash',
    onClick: () => onAddClick(true),
  },
]

const dataSource = useODataSource(accountingApi.getAbsoluteUrl() + accountingApi.budgetDocumentsUrl, {
  onChanged: () => {
    isEmptyDataSource.value = dataSource.totalCount() === 0
  },
  searchExpr: 'search_field',
})

const updateDatasource = () => dataSource.reload()

const route = useRoute()
const router = useRouter()

const openRouteQuoteAsync = async () => {
  const { year_mnemonic: year, number_mnemonic: number, ...query } = route.query
  if (!year || !number) return
  router.replace({ query })
  const [quote] = await dataSource.store().load({ filter: [['year_mnemonic', '=', Number(year)], 'and', ['number_mnemonic', '=', Number(number)]] })
  if (quote) itemDetailId.value = quote.id
}
watch(() => [route.query.year_mnemonic, route.query.number_mnemonic], openRouteQuoteAsync, { immediate: true })

const onDetailUpdate = () => {
  itemDetailId.value = null
  updateDatasource()
}

const getRandomNumber = () => (Math.floor(Math.random() * 100) % 5) + 1

const onEditItemChanged = () => {
  editItem.value = null
  updateDatasource()
}

const onItemClick = (itemId) => {  
  itemDetailId.value = itemId 
  updateDatasource()
}


useEntityChanges([entityChangeTypeEnum.QUOTE], updateDatasource, () => editItem.value !== null)
</script>
<template>
  <div>
    <filter-sort-selector
      :data-source="dataSource"
      :kpi-enabled="true"
      :kpi-visible="webSiteStore.isKpiBarVisible('quotes')"
      @toggle-kpi="webSiteStore.toggleKpiBar('quotes')"
    />
    <signal-bar page="quotes" embedded :visible="webSiteStore.isKpiBarVisible('quotes')" />
    <q-dialog
      maximized
      :model-value="!!editItem"
    >
      <budget-document-edit
        v-if="!!editItem"
        :model-value="editItem"
        :initial-quick-create="quickCreate"
        @cancel="onEditItemChanged"
        @delete="onEditItemChanged"
        @update="onEditItemChanged"
      />
    </q-dialog>
    <q-dialog
      maximized
      :model-value="!!itemDetailId"
    >
      <budget-document-detail
        v-if="!!itemDetailId"
        :item-id="itemDetailId"
        @cancel="onDetailUpdate"
        @update="onDetailUpdate"
      />
    </q-dialog>
    <q-card flat class="q-pa-none">
      <q-card-section class="q-pa-none">
        <dx-list
          v-show="!isEmptyDataSource"
          :data-source="dataSource"
          height="calc(100vh - 8rem)"
          page-load-mode="scrollBottom"
          class="events-list"
          item-template="item"
          selection-mode="none"
          :active-state-enabled="false"
        >
          <template #item="{ data: item }">
            <budget-document-list-item
              :item="item"
              @click="onItemClick"
            />
          </template>
        </dx-list>
        <div
          v-show="isEmptyDataSource"
          class="empty-container"
        >
          <img
            src="/img/no-documents.svg"
            :style="{ maxWidth: '80vw', objectFit: 'contain' }"
            class="q-mb-xl"
          >
          <p class="text-h5">
            {{ $t('common.noBudgetDocument') }}
          </p>
          <p class="sub text-weight-light">
            {{ $t(`common.noBudgetDocument${getRandomNumber()}`) }}
          </p>
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>
